<template>
  <div :class="`container stripe-payment-form ${isIframed ? '' : 'mt-5'}`">
    <div class="row justify-content-center">
      <div :class="`col-md-${ isIframed ? '12' : '6'}`">
        <div class="card shadow">
          <div class="card-header bg-primary text-white">
            <h4 class="mb-0 text-white">Complete Payment</h4>
          </div>
          <div class="card-body">
            <div v-show="loadingInfo" class="text-center p-2">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2">Loading payment details...</p>
            </div>

            <div v-show="!loadingInfo">
              <form id="payment-form" @submit.prevent="handleSubmit">
                <b-row>
                  <b-col md="12">
                    <div class="payment-info bg-light rounded">
                      <div class="mt-1 p-1">
                        <h4>Payment Details</h4>
                        <span><strong>Amount:</strong> {{ formattedAmount }}</span>
                      </div>
                    </div>
                  </b-col>
                  <b-col>
                    <div class="mt-1">
                      <div class="mb-1 border rounded p-1 mt-1">
                        <span class="text-dark">Email address</span>
                        <input type="email" id="customer-email" class="form-control" v-model="emailValue" placeholder="email@example.com" required>
                      </div>

                      <div id="payment-element" class="mb-1" style="font-size: 10px !important;"></div>
                    </div>
                  </b-col>
                  <b-col md="6">
                    <div class="border rounded p-1 mt-1 mb-4">
                      <h5>Contact Information</h5>
                      <div id="address-element" style="font-size: 10px !important;">
                      </div>
                    </div>
                  </b-col>

                  <b-col md="12">
                    <div class="mt-1">
                      <div id="processing-notice" class="text-center d-none mb-3">
                        <p class="text-muted small">Verifying your payment, please do not close this window...</p>
                      </div>

                      <button :disabled="isProcessing" class="btn btn-primary btn-lg w-100">
                        <span v-if="isProcessing" class="spinner-border spinner-border-sm mr-2"></span>
                        {{ isProcessing ? 'Processing...' : 'Pay Now' }}
                      </button>

                      <div v-if="errorMessage" class="alert alert-danger mt-2">
                        {{ errorMessage }}
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import { mapActions } from 'vuex'

export default {
  name: 'StripePayment',
  data() {
    return {
      stripe: null,
      elements: null,
      isProcessing: false,
      errorMessage: "",
      loadingInfo: true,
      processComplete: false,
      emailValue: null,
			isIframed: window.parent !== window,
      // Extraemos parámetros de la URL directamente
      sessionId: new URLSearchParams(window.location.search).get('session_id'),
      clientSecret: new URLSearchParams(window.location.search).get('client_secret'),
      amountInCents: new URLSearchParams(window.location.search).get('amount'),
      currency: new URLSearchParams(window.location.search).get('currency') || 'USD'
    };
  },
  computed: {
    formattedAmount() {
      const amount = parseFloat(this.amountInCents) / 100;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: this.currency.toUpperCase(),
      }).format(amount) + ` ${this.currency.toUpperCase()}`;
    }
  },
  async mounted() {
    await this.initStripe();
  },
  methods: {
    ...mapActions('shop', ['processStripePayment']),
    async initStripe() {
      try {
        // Inicializar Stripe con tu llave pública
        this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY, { maxNetworkRetries: 2 });

        if (!this.clientSecret) {
          this.errorMessage = "Missing payment information (Client Secret).";
          this.loadingInfo = false;
          return;
        }

        const loadTimeout = setTimeout(() => {
          const el = document.getElementById("payment-element");
          if (el && el.innerHTML.trim() === "") {
            showMessage("Connection is slow. If the form doesn't appear, please refresh.");
          }
        }, 15000);

        // Configurar elementos con idioma automático
        this.elements = this.stripe.elements({
          clientSecret: this.clientSecret,
          locale: 'auto',
          appearance: { theme: 'stripe' }
        });

        // Crear y montar el Payment Element
        const addressElement = this.elements.create('address', {
          mode: 'billing',
        });
        const paymentElement = this.elements.create("payment", {
          layout: "accordion",
          paymentMethodOrder: ['card'],
          terms: {
            card: 'never',
          },
          fields: {
            billingDetails: {
              email: 'auto',
            }
          },
          wallets: {
            applePay: 'never',
            googlePay: 'never',
            link: 'never'
          }
        });

        paymentElement.on('change', (event) => {
          if (event.error && event.error.type !== 'network_error') {
            showMessage(event.error.message);
          }
        });

        paymentElement.on('ready', () => clearTimeout(loadTimeout));
        addressElement.mount('#address-element');
        paymentElement.mount("#payment-element");

        this.loadingInfo = false;
      } catch (error) {
        console.error("Stripe Init Error:", error);
        this.errorMessage = "Failed to load payment form.";
        this.loadingInfo = false;
      }
    },
    async handleSubmit() {
      try{
        if (this.isProcessing) return;

        this.isProcessing = true;
        this.errorMessage = "";

        const stripeResponse = await this.stripe.confirmPayment({
          elements: this.elements,
          redirect: 'if_required', // Esto previene redirects automáticos excepto cuando son absolutamente necesarios
          confirmParams: {
            receipt_email: this.emailValue
          },
        });

        const { error, paymentIntent } = stripeResponse

        // Si hay error, se detiene aquí. Si no, Stripe redirige automáticamente.
        if (error) {
          if (error.type === "card_error" || error.type === "validation_error") {
            this.errorMessage = error.message;
          } else {
            this.errorMessage = "An unexpected error occurred.";
          }
          this.isProcessing = false;
        }

        if (paymentIntent) {
          await this.handlePaymentStatus(paymentIntent);
        }

      } catch (err) {
        console.error("Payment confirmation error:", err);
        this.isProcessing = false;
        this.errorMessage = "An error occurred during payment processing.";
      } finally {
        this.processComplete = true;
      }
    },

    // Método para manejar pagos exitosos
    async handlePaymentStatus(paymentIntent) {
      const paymentUI = document.getElementById("payment-element");
      const processingNotice = document.getElementById("processing-notice");
      try {

        if (paymentUI) paymentUI.classList.add("d-none");
        if (processingNotice) processingNotice.classList.remove("d-none");

        // Va y procesa el pago en Salescore
        const callbackData = await this.processStripePayment({
          paymentIntentId: paymentIntent.id,
          sessionId: this.sessionId,
        });

        // Redirigir manualmente o mostrar mensaje de éxito
        if (callbackData?.status && !!callbackData?.redirectUrl) window.location.href = callbackData.redirectUrl;
        else {
          if (paymentUI) paymentUI.classList.remove("d-none");
          if (processingNotice) processingNotice.classList.add("d-none");
          throw new Error("Cannot get callback url");
        }
      } catch (error) {
        console.error("Error processing payment success:", error);
        this.errorMessage = "Payment was successful, but there was an error updating your order.";
        if (paymentUI) paymentUI.classList.remove("d-none");
        if (processingNotice) processingNotice.classList.add("d-none");
        this.isProcessing = false;
      }
    },

  }
};
</script>


<style scoped>
.stripe-payment-form{
  font-size: 14px;
}
</style>